import { ReactNode, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'react-bootstrap-icons';

interface PageHeaderProps {
  title: string;
  /**
   * One quiet line of fact under the title: what a tool changes, or which
   * client and image a run is for. Not a slogan: a page everybody knows needs none.
   */
  description?: ReactNode;
  /** Buttons and controls on the right of the title. */
  actions?: ReactNode;
  /** A link back to the page this one belongs under, shown above the title. */
  back?: { to: string; label: string };
}

/**
 * The one page header: the page's name on the left, its own controls on the
 * right, in one compact row.
 *
 * It is drawn into a slot directly under the top bar (`#portal-page-header` in
 * App), not where the page mounts it. That keeps the title in the same place
 * on every page, whatever width the page lays its content out at.
 */
export function PageHeader({ title, description, actions, back }: PageHeaderProps) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  // The slot is in the document by the time a layout effect runs, never before a first render.
  useLayoutEffect(() => setSlot(document.getElementById('portal-page-header')), []);

  const header = (
    <div className="page-header">
      <div className="page-header__text">
        {back && (
          <Link to={back.to} className="page-header__back">
            <ArrowLeft aria-hidden="true" />
            {back.label}
          </Link>
        )}
        <h1 className="page-header__title">{title}</h1>
        {description && <p className="page-header__description">{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </div>
  );

  return slot ? createPortal(header, slot) : null;
}

export default PageHeader;
